import Handsontable from 'handsontable';
import { as400ColumnEditKey } from '@erp/rpc';
import type { As400ExportColumn } from '@erp/rpc';
import {
  selectedCells,
  writableCellAt,
  type BlankMarkerFor,
  type GridCell,
  type WritableCellAt,
} from './as400GridSelection';

/**
 * The blank-out marker of each of `columns`, looked up by the field a grid
 * column is bound to (its edit key). Columns without one — and keyless
 * spacers — have none.
 */
export function blankMarkerLookup(
  columns: readonly As400ExportColumn[],
): BlankMarkerFor {
  const markers = new Map<string, string>();
  for (const column of columns) {
    const editKey = as400ColumnEditKey(column);
    if (editKey && column.blankMarker) {
      markers.set(editKey, column.blankMarker);
    }
  }
  return (prop) => markers.get(prop);
}

/** One cell write: `[row, prop, value]`, as `setDataAtRowProp` takes it. */
type CellWrite<Value> = [row: number, prop: string, value: Value];

/** One cell cleared out to its column's marker: `[row, prop, marker]`. */
export type CellBlankOut = CellWrite<string>;

/** One cell Delete writes: its column's marker, or null to empty it. */
export type CellDelete = CellWrite<string | null>;

/**
 * A write for every writable cell among `cells` (read-only, hidden and
 * synthetic cells skipped), valued by its field; `undefined` writes nothing.
 */
function writableCellWrites<Value>(
  cells: readonly GridCell[],
  writableAt: WritableCellAt,
  valueFor: (prop: string) => Value | undefined,
): Array<CellWrite<Value>> {
  const writes: Array<CellWrite<Value>> = [];
  for (const cell of cells) {
    const target = writableAt(cell);
    const value = target ? valueFor(target.prop) : undefined;
    if (target && value !== undefined) {
      writes.push([cell.row, target.prop, value]);
    }
  }
  return writes;
}

/**
 * The writes that clear every writable cell among `cells` out to its own
 * column's AS/400 blank-out marker. Cells the grid can't write (read-only,
 * hidden, synthetic) and cells whose column documents no marker are skipped.
 */
export function cellBlankOuts(
  cells: readonly GridCell[],
  writableAt: WritableCellAt,
  blankMarkerFor: BlankMarkerFor,
): CellBlankOut[] {
  return writableCellWrites(cells, writableAt, blankMarkerFor);
}

/**
 * What Delete writes over `cells`: the clear-out marker where the column has
 * one, and an empty cell — Delete's ordinary effect — everywhere else it can
 * write, so Delete never does less than it did.
 */
export function cellDeletes(
  cells: readonly GridCell[],
  writableAt: WritableCellAt,
  blankMarkerFor: BlankMarkerFor,
): CellDelete[] {
  return writableCellWrites(
    cells,
    writableAt,
    (prop) => blankMarkerFor(prop) ?? null,
  );
}

// The change source a clear-out writes under. Not 'edit', so typing's
// widening to the whole selection leaves it be: each cell already gets its
// own column's marker. It still goes through the caps and ASCII rewrites,
// which leave `/` as it is, and through afterChange staging like typing.
export const BLANK_OUT_SOURCE = 'As400Grid.blankOut';

/** Clear the live grid's selection out to its markers: the menu item's write. */
export function blankOutSelection(
  instance: Handsontable,
  blankMarkerFor: BlankMarkerFor,
): void {
  const writes = cellBlankOuts(
    selectedCells(instance),
    writableCellAt(instance),
    blankMarkerFor,
  );
  if (writes.length > 0) instance.setDataAtRowProp(writes, BLANK_OUT_SOURCE);
}

/**
 * A `beforeKeyDown` handler: Delete over a selection (no editor open) clears
 * marker columns out to their marker, as the "Clear out" menu item does, and
 * empties the rest in the same batch. A selection with no marker cell is left
 * to Handsontable's own Delete.
 *
 * Only Delete: in a change-mode upload an empty cell means "do not change",
 * so Delete — the key for "clear this" — writes what actually clears the
 * field on the AS/400. Backspace keeps Handsontable's plain emptying, so an
 * empty ("do not change") cell is still one key away.
 */
export function blankOutOnDelete(
  instance: Handsontable,
  event: KeyboardEvent,
  blankMarkerFor: BlankMarkerFor,
): void {
  if (event.key !== 'Delete' || instance.getActiveEditor()?.isOpened()) return;
  const writes = cellDeletes(
    selectedCells(instance),
    writableCellAt(instance),
    blankMarkerFor,
  );
  if (!writes.some(([, , value]) => value !== null)) return;
  // Handsontable's own flag, not the DOM method: its shortcut manager checks
  // the flag, and runs its Delete (emptying the cells) after the native one.
  Handsontable.dom.stopImmediatePropagation(event);
  event.preventDefault();
  instance.setDataAtRowProp(writes, BLANK_OUT_SOURCE);
}
